// Shapes the palette's flat field list into per-team scopes of single chips plus
// stat/player grids. Structure comes from field keys; labels come from the backend,
// normalised to sentence case. A key no pattern recognises stays a plain chip, so a
// field the backend adds later never drops out of the palette.

const ACRONYMS = new Set(['FG', 'FT', 'PIR', 'MVP', 'OT'])

const GENERAL_KEYS = new Set([
  'competition_logo', 'competition_name', 'game_state', 'sponsors',
  'clock', 'period', 'shotclock', 'title', 'quarter',
])

const SIDE = /^(home|away)_(.+)$/
const PLAYER = /^player_(\d+)_(photo|name|jersey|position|height|age)$/
const STAT = /^(two_pts|three_pts|2fg|3fg|ft|assists|rebounds|off_rebounds|def_rebounds|blocks|steals|turnovers)(?:_(ratio|pct|bar_pct))?$/

const METRICS = {
  two_pts: '2FG', '2fg': '2FG', three_pts: '3FG', '3fg': '3FG', ft: 'FT',
  assists: 'Assists', rebounds: 'Rebounds', off_rebounds: 'Off. rebounds',
  def_rebounds: 'Def. rebounds', blocks: 'Blocks', steals: 'Steals', turnovers: 'Turnovers',
}
const METRIC_ORDER = ['2FG', '3FG', 'FT', 'Assists', 'Rebounds', 'Off. rebounds', 'Def. rebounds', 'Blocks', 'Steals', 'Turnovers']

// [id, header, full name]
const STAT_COLUMNS = [['value', 'Value', 'Value'], ['pct', '%', 'Percentage'], ['bar', 'Bar', 'Bar']]
const PLAYER_COLUMNS = [
  ['photo', 'Photo', 'Photo'], ['name', 'Name', 'Name'], ['jersey', '#', 'Jersey'],
  ['position', 'Pos', 'Position'], ['height', 'Ht', 'Height'], ['age', 'Age', 'Age'],
]
const STAT_COLUMN_OF = { pct: 'pct', bar_pct: 'bar' }

const STRIP = { home: /^home( team)? /i, away: /^away( team)? /i, team: /^team /i }

export function sentenceCase(label) {
  const text = String(label ?? '').trim().split(/\s+/).map((word) => {
    if (/\d/.test(word) && /[a-z]/i.test(word)) return word.toUpperCase()
    if (ACRONYMS.has(word.replace(/[^a-z]/gi, '').toUpperCase())) return word.toUpperCase()
    return word.toLowerCase()
  }).join(' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const chipLabel = (field, strip) => {
  const full = sentenceCase(field.label ?? field.key)
  const short = strip ? full.replace(strip, '') : full
  return short ? short.charAt(0).toUpperCase() + short.slice(1) : full
}

const buildGrid = (id, title, corner, columnDefs, rows, scopeTitle) => {
  const used = columnDefs.filter(([col]) => rows.some((r) => r.cells[col]))
  return {
    id,
    title,
    corner,
    columns: used.map(([col, label, name]) => ({ id: col, label, name })),
    rows: rows.map((r) => ({
      id: r.id,
      label: r.label,
      cells: used.map(([col, , name]) => r.cells[col]
        ? { field: r.cells[col], title: `${scopeTitle} · ${r.name} · ${name}` }
        : null),
    })),
  }
}

// -> { scopes, hasSwitch, wide }
//   scopes: [{ id, title, chips: [{ field, label }], grids: [...] }] in display order
//   hasSwitch: home/away scopes and a grid to pick between, so one team shows at a time
//   wide: a player grid needs the wider palette
export function buildPalette(fields) {
  const single = fields.some((f) => f.key === 'player_photo')
  const titles = { general: 'General', home: 'Home team', away: 'Away team', team: single ? 'Player' : 'Team' }
  const raw = new Map()
  const scopeOf = (id) => {
    if (!raw.has(id)) raw.set(id, { id, chips: [], stats: new Map(), players: new Map() })
    return raw.get(id)
  }
  const addRow = (rows, id, label, name, col, field) => {
    if (!rows.has(id)) rows.set(id, { id, label, name, cells: {} })
    rows.get(id).cells[col] = field
  }

  fields.forEach((field) => {
    const side = SIDE.exec(field.key)
    const id = side ? side[1] : GENERAL_KEYS.has(field.key) ? 'general' : 'team'
    const rest = side ? side[2] : field.key
    const scope = scopeOf(id)
    const player = id !== 'general' && PLAYER.exec(rest)
    const stat = id !== 'general' && STAT.exec(rest)
    if (player) {
      addRow(scope.players, Number(player[1]), String(Number(player[1])), `Player ${Number(player[1])}`, player[2], field)
    } else if (stat) {
      const metric = METRICS[stat[1]]
      addRow(scope.stats, metric, metric, metric, STAT_COLUMN_OF[stat[2]] ?? 'value', field)
    } else {
      scope.chips.push(field)
    }
  })

  const scopes = ['general', 'home', 'away', 'team'].filter((id) => raw.has(id)).map((id) => {
    const scope = raw.get(id)
    const title = titles[id]
    const strip = id === 'team' && single ? null : STRIP[id]
    const chipFields = [...scope.chips]
    const grids = []

    const players = [...scope.players.values()].sort((a, b) => a.id - b.id)
    if (players.length >= 2) grids.push(buildGrid('players', 'Players', 'Player', PLAYER_COLUMNS, players, title))
    else players.forEach((r) => chipFields.push(...Object.values(r.cells)))

    const stats = [...scope.stats.values()].sort((a, b) => METRIC_ORDER.indexOf(a.id) - METRIC_ORDER.indexOf(b.id))
    if (stats.length >= 2) grids.push(buildGrid('stats', 'Stats', 'Stat', STAT_COLUMNS, stats, title))
    else stats.forEach((r) => chipFields.push(...Object.values(r.cells)))

    return {
      id,
      title,
      chips: chipFields.map((field) => ({ field, label: chipLabel(field, strip) })),
      grids,
    }
  })

  const sided = scopes.filter((s) => s.id === 'home' || s.id === 'away')
  return {
    scopes,
    hasSwitch: sided.length === 2 && sided.some((s) => s.grids.length > 0),
    wide: scopes.some((s) => s.grids.some((g) => g.id === 'players')),
  }
}
